import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import axiosClient from "../../../../api-client/AxiosClient";
import { toast } from "react-toastify";
import Select, { components } from "react-select";
import { Trash2 } from "lucide-react";
import { FaEdit } from "react-icons/fa";

// Helper function for button hover handlers
const createHoverHandlers = (hoverColor, defaultColor, hoverTextColor = null, defaultTextColor = null) => ({
  onMouseOver: (e) => {
    e.currentTarget.style.backgroundColor = hoverColor;
    if (hoverTextColor) e.currentTarget.style.color = hoverTextColor;
  },
  onMouseOut: (e) => {
    e.currentTarget.style.backgroundColor = defaultColor;
    if (defaultTextColor) e.currentTarget.style.color = defaultTextColor;
  },
  onFocus: (e) => {
    e.currentTarget.style.backgroundColor = hoverColor;
    if (hoverTextColor) e.currentTarget.style.color = hoverTextColor;
  },
  onBlur: (e) => {
    e.currentTarget.style.backgroundColor = defaultColor;
    if (defaultTextColor) e.currentTarget.style.color = defaultTextColor;
  },
});

// Helper functions for status styling
const getStatusBackgroundColor = (isActive) => {
  return isActive ? "#dcfce7" : "#fee2e2";
};

const getStatusTextColor = (isActive) => {
  return isActive ? "#16a34a" : "#dc2626";
};

const getStatusBorderColor = (isActive) => {
  return isActive ? "#bbf7d0" : "#fecaca";
};

const getStatusText = (isActive) => {
  return isActive ? "Active" : "Inactive";
};

// Custom Option component with checkbox
const Option = (props) => {
  const selectedValues = props.selectProps.value || [];
  const isSelected = Array.isArray(selectedValues)
    ? selectedValues.some((item) => item.value === props.value)
    : props.isSelected;

  return (
    <components.Option {...props}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <input
          type="checkbox"
          checked={isSelected}
          readOnly
          style={{
            width: "16px",
            height: "16px",
            cursor: "pointer",
            flexShrink: 0,
          }}
        />
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {props.label}
        </span>
      </div>
    </components.Option>
  );
};

Option.propTypes = {
  selectProps: PropTypes.shape({
    value: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
    })),
  }),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isSelected: PropTypes.bool,
  label: PropTypes.string.isRequired,
};

// Custom MenuList component with Select All option
const MenuList = (props) => {
  const { options, getValue, setValue } = props;
  const selectedValues = getValue();
  const allSelected = selectedValues.length === options.length;

  const handleSelectAll = () => {
    if (allSelected) {
      setValue([], "clear");
    } else {
      setValue(options, "set-value");
    }
  };

  return (
    <components.MenuList {...props}>
      <button
        type="button"
        onClick={handleSelectAll}
        style={{
          width: "100%",
          padding: "10px 12px",
          cursor: "pointer",
          border: "none",
          borderBottom: "1px solid #e5e7eb",
          backgroundColor: "#f9fafb",
          fontWeight: "600",
          color: "#1e40af",
          textAlign: "left",
        }}
        {...createHoverHandlers("#eff6ff", "#f9fafb")}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <input
            type="checkbox"
            checked={allSelected}
            readOnly
            style={{
              width: "16px",
              height: "16px",
              cursor: "pointer",
              flexShrink: 0,
            }}
          />
          <span>Select All</span>
        </div>
      </button>
      {props.children}
    </components.MenuList>
  );
};

MenuList.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
  getValue: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

// Custom ValueContainer to show only 4 items
const ValueContainer = ({ children, ...props }) => {
  const selectedValues = props.getValue();
  const MAX_VISIBLE = 4;

  if (!selectedValues || selectedValues.length === 0) {
    return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
  }

  const hiddenCount = selectedValues.length - MAX_VISIBLE;

  return (
    <components.ValueContainer {...props}>
      {React.Children.toArray(children).map((child, index) => {
        if (child?.type === components.MultiValue) {
          const valueIndex = selectedValues.findIndex(
            (v) => v.value === child.props.data.value
          );
          if (valueIndex >= MAX_VISIBLE) {
            return null;
          }
        }
        return child;
      })}
      {hiddenCount > 0 && (
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "3px 8px",
            backgroundColor: "#f3f4f6",
            borderRadius: "6px",
            fontSize: "13px",
            color: "#6b7280",
            fontWeight: "600",
            marginLeft: "4px",
          }}
        >
          +{hiddenCount} more
        </div>
      )}
    </components.ValueContainer>
  );
};

ValueContainer.propTypes = {
  children: PropTypes.node.isRequired,
  getValue: PropTypes.func.isRequired,
};

// Professional Modal Component
const UserModal = ({ show, title, onClose, children }) => {
  if (!show) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: "20px",
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          width: "100%",
          maxWidth: "600px",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: "24px 32px",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: "24px",
              fontWeight: "700",
              color: "#111827",
            }}
          >
            {title}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "28px",
              cursor: "pointer",
              color: "#6b7280",
              lineHeight: 1,
              padding: 0,
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "6px",
              transition: "all 0.2s",
            }}
            {...createHoverHandlers("#f3f4f6", "transparent")}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {/* Modal Body */}
        <div
          style={{
            padding: "20px 24px",
            overflowY: "auto",
            flex: 1,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

UserModal.propTypes = {
  show: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

const getRoleIdFromRoles = (roles, roleName) => {
  const role = roles.find(
    (r) => r.name?.toLowerCase() === roleName?.toLowerCase()
  );
  return role?.id;
};

// Helper function to initialize edit form
const initializeEditForm = (user, roles) => {
  const selectedUserRole = getRoleIdFromRoles(roles, user.role);
  return {
    email: user.email || "",
    firstName: user.first_name || "",
    lastName: user.last_name || "",
    role: selectedUserRole || "",
    organisation: user.organisation || "",
    isActive: user.isactive.toString(),
  };
};

// Helper function to reset form
const getResetForm = () => ({
  email: "",
  firstName: "",
  lastName: "",
  role: "",
  organisation: "",
  isActive: "true",
});

// Helper function to handle edit button click
const handleEditClick = (user, roles, setters) => {
  const { setShowAdd, setEditUserId, setAddForm, setSelectedOrganizations, setSelectedObjects } = setters;
  setShowAdd(true);
  setEditUserId(user.id);
  setAddForm(initializeEditForm(user, roles));
  setSelectedOrganizations(user.orgList || []);
  setSelectedObjects(user.objList || []);
};

// Helper function to handle delete button click
const handleDeleteClick = (userId, setters) => {
  const { setEditUserId, setShowDeleteModal } = setters;
  setEditUserId(userId);
  setShowDeleteModal(true);
};

export default function UserManagement() {
  const { UserMgmt } = JSON.parse(
    sessionStorage.getItem("rolesAndPermission") || "{}"
  );

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [sortAsc, setSortAsc] = useState(true);
  const [page, setPage] = useState(1);
  const [selectedObjects, setSelectedObjects] = useState([]);
  const [allObjects, setAllObjects] = useState([]);
  const [objectsLoading, setObjectsLoading] = useState(false);
  const pageSize = 5;

  const [addForm, setAddForm] = useState(getResetForm());
  const [editUserId, setEditUserId] = useState(null);
  const [addLoading, setAddLoading] = useState(false);
  const [selectedOrganizations, setSelectedOrganizations] = useState([]);
  const [allOrganizations, setAllOrganizations] = useState([]);

  // Create setters object for helper functions
  const formSetters = {
    setShowAdd,
    setEditUserId,
    setAddForm,
    setSelectedOrganizations,
    setSelectedObjects,
    setShowDeleteModal,
  };

  // Add this validation function after formSetters
  const isFormValid = () => {
    return (
      addForm.email.trim() !== "" &&
      addForm.firstName.trim() !== "" &&
      addForm.lastName.trim() !== "" &&
      addForm.role !== "" &&
      selectedOrganizations.length > 0 &&
      addForm.isActive !== ""
    );
  };

  // Fetch functions
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axiosClient.get("/api/User/all");
      setUsers(response.data || []);
      setError(null);
    } catch (err) {
      const errorMessage = err?.response?.data?.message || "Failed to load user data.";
      setError(errorMessage);
      console.error("Error fetching users:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchObjectsByCorporations = async (corporationIds) => {
    try {
      setObjectsLoading(true);
      if (!corporationIds || corporationIds.length === 0) {
        setAllObjects([]);
        setSelectedObjects([]);
        return;
      }

      const allObjectsPromises = corporationIds.map(async (corpId) => {
        try {
          const response = await axiosClient.get(
            `/api/Object/GetObjectByOrgId?OrganizationId=${corpId}`
          );
          return (
            response.data?.map((obj) => ({
              value: obj.objectid,
              label: obj.description,
              name: obj.name,
              corporationId: corpId,
            })) || []
          );
        } catch (error) {
          console.error(
            "Error fetching objects for organization:",
            corpId,
            error
          );
          return [];
        }
      });

      const objectsArrays = await Promise.all(allObjectsPromises);
      const allObjectsFlattened = objectsArrays.flat();
      const uniqueObjects = allObjectsFlattened.filter(
        (obj, index, self) =>
          index === self.findIndex((o) => o.value === obj.value)
      );

      setAllObjects(uniqueObjects);
    } catch (error) {
      const errorMessage = error?.response?.data?.message || "Failed to load objects. Please try again.";
      toast.error(errorMessage);
      console.error("Error fetching objects:", error);
    } finally {
      setObjectsLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await axiosClient.get(
        "/api/Organization/GetUserOrganizations"
      );
      const options = response.data?.map((item) => ({
        value: item.organizationId,
        label: item.name,
      }));
      setAllOrganizations(options || []);
    } catch (error) {
      console.error("Error fetching organizations:", error);
      setAllOrganizations([]);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axiosClient.get("/api/User/Roles");
      setRoles(response.data || []);
    } catch (error) {
      console.error("Error fetching roles:", error);
      setRoles([]);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchOrganizations();
  }, []);

  useEffect(() => {
    if (selectedOrganizations && selectedOrganizations.length > 0) {
      const corporationIds = selectedOrganizations.map((org) => org.value);
      fetchObjectsByCorporations(corporationIds);
    } else {
      setAllObjects([]);
      setSelectedObjects([]);
    }
  }, [selectedOrganizations]);

  // Filter and sort users
  const filteredUsers = users.filter((u) =>
    `${u.first_name || ""} ${u.last_name || ""}`
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const nameA = `${a.first_name || ""} ${a.last_name || ""}`.toLowerCase();
    const nameB = `${b.first_name || ""} ${b.last_name || ""}`.toLowerCase();
    if (nameA < nameB) return sortAsc ? -1 : 1;
    if (nameA > nameB) return sortAsc ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(sortedUsers.length / pageSize);
  const pagedUsers = sortedUsers.slice((page - 1) * pageSize, page * pageSize);

  // Helper function to get colSpan for table cells
  const getTableColSpan = () => {
    return (UserMgmt?.canDelete || UserMgmt?.canUpdate) ? 6 : 5;
  };

  // Helper function to render users table content
  const renderUsersTableContent = () => {
    if (pagedUsers.length === 0) {
      return (
        <tr>
          <td
            colSpan={getTableColSpan()}
            style={{
              padding: "48px",
              textAlign: "center",
              color: "#9ca3af",
            }}
          >
            No users found.
          </td>
        </tr>
      );
    }

    return pagedUsers.map((user, idx) => (
      <tr
        key={user.id}
        style={{
          backgroundColor: idx % 2 === 0 ? "white" : "#f9fafb",
          transition: "background-color 0.2s",
        }}
        {...createHoverHandlers("#eff6ff", idx % 2 === 0 ? "white" : "#f9fafb")}
      >
        <td
          style={{
            padding: "16px 20px",
            color: "#111827",
            fontWeight: "500",
            borderBottom:
              idx === pagedUsers.length - 1
                ? "none"
                : "1px solid #e5e7eb",
          }}
        >
          {`${user.first_name || ""} ${user.last_name || ""}`.trim()}
        </td>
        <td
          style={{
            padding: "16px 20px",
            color: "#6b7280",
            borderBottom:
              idx === pagedUsers.length - 1
                ? "none"
                : "1px solid #e5e7eb",
          }}
        >
          {user.email}
        </td>
        <td
          style={{
            padding: "16px 20px",
            borderBottom:
              idx === pagedUsers.length - 1
                ? "none"
                : "1px solid #e5e7eb",
          }}
        >
          <span
            style={{
              display: "inline-block",
              padding: "4px 12px",
              borderRadius: "12px",
              fontSize: "12px",
              fontWeight: "600",
              backgroundColor: "#dbeafe",
              color: "#1e40af",
              border: "1px solid #bfdbfe",
            }}
          >
            {user.role}
          </span>
        </td>
        <td
          style={{
            padding: "16px 20px",
            borderBottom:
              idx === pagedUsers.length - 1
                ? "none"
                : "1px solid #e5e7eb",
          }}
        >
          <span
            style={{
              display: "inline-block",
              padding: "4px 12px",
              borderRadius: "12px",
              fontSize: "12px",
              fontWeight: "600",
              backgroundColor: getStatusBackgroundColor(user.isactive),
              color: getStatusTextColor(user.isactive),
              border: `1px solid ${getStatusBorderColor(user.isactive)}`,
            }}
          >
            {getStatusText(user.isactive)}
          </span>
        </td>
        <td
          style={{
            padding: "16px 20px",
            color: "#6b7280",
            borderBottom:
              idx === pagedUsers.length - 1
                ? "none"
                : "1px solid #e5e7eb",
          }}
        >
          {user.created_at}
        </td>
        {(UserMgmt?.canDelete || UserMgmt?.canUpdate) && (
          <td
            style={{
              padding: "16px 20px",
              borderBottom:
                idx === pagedUsers.length - 1
                  ? "none"
                  : "1px solid #e5e7eb",
            }}
          >
            <div style={{ display: "flex", gap: "8px" }}>
              {UserMgmt?.canUpdate && (
                <button
                  onClick={() => handleEditClick(user, roles, formSetters)}
                  style={{
                    padding: "8px",
                    borderRadius: "9999px",
                    border: "none",
                    backgroundColor: "transparent",
                    color: "#3b82f6",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  {...createHoverHandlers("#dbeafe", "transparent", "#2563eb", "#3b82f6")}
                  title="Edit User"
                >
                  <FaEdit size={15} />
                </button>
              )}
              {UserMgmt?.canDelete && (
                <button
                  onClick={() => handleDeleteClick(user.id, formSetters)}
                  style={{
                    padding: "8px",
                    borderRadius: "9999px",
                    border: "none",
                    backgroundColor: "transparent",
                    color: "#ef4444",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  {...createHoverHandlers("#fee2e2", "transparent", "#dc2626", "#ef4444")}
                  title="Delete User"
                >
                  <Trash2 size={15} />
                </button>
              )}
            </div>
          </td>
        )}
      </tr>
    ));
  };

  // Handlers
  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleDeleteUser = async () => {
    try {
      await axiosClient.delete(`/api/User/${editUserId}`);
      setShowDeleteModal(false);
      toast.success("User deleted successfully.");
      fetchUsers();
    } catch (err) {
      const errorMessage = err?.response?.data?.message || "Something went wrong while deleting user. Please try again later!";
      toast.error(errorMessage);
      console.error("Error deleting user:", err);
    }
  };

  const handleSubmit = async () => {
    setAddLoading(true);
    const orgValuesString = selectedOrganizations
      ?.map((item) => item.value)
      .join(", ");

    try {
      const payload = {
        email: addForm.email,
        password: "",
        firstName: addForm.firstName,
        lastName: addForm.lastName,
        role: addForm.role,
        organisation: orgValuesString,
        // objects: selectedObjects?.map((item) => item.value).join(", "), // Temporarily disabled - passing empty string instead
        objects: "abc,xyz",// Temporarily set to empty string - Objects functionality commented out
        isActive: addForm.isActive === "true",
      };

      if (editUserId) {
        await axiosClient.put(`/api/User/${editUserId}`, payload);
        toast.success("User updated successfully.");
      } else {
        await axiosClient.post("/api/User", payload);
        toast.success("User created successfully.");
      }

      setShowAdd(false);
      setAddForm({
        email: "",
        firstName: "",
        lastName: "",
        role: "",
        organisation: "",
        isActive: "true",
      });
      setSelectedOrganizations([]);
      setSelectedObjects([]);
      setEditUserId(null);
      fetchUsers();
    } catch (err) {
      const errorMessage = err?.response?.data?.message || "Failed to save user. Please check the details.";
      toast.error(errorMessage);
      console.error("Error saving user:", err);
    } finally {
      setAddLoading(false);
    }
  };

  // Professional Select Styles
  const selectStyles = {
    container: (base) => ({ ...base, width: "100%" }),
    control: (base, state) => ({
      ...base,
      minHeight: "44px",
      borderRadius: "8px",
      borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(59, 130, 246, 0.1)" : "none",
      "&:hover": { borderColor: "#3b82f6" },
      backgroundColor: state.isDisabled ? "#f9fafb" : "white",
      cursor: state.isDisabled ? "not-allowed" : "default",
      outline: "none",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
      "input:focus": {
        boxShadow: "none",
        outline: "none",
      },
    }),
    menu: (base) => ({ ...base, zIndex: 10001 }),
    menuList: (base) => ({ ...base, maxHeight: "200px" }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? "#eff6ff" : "white",
      color: state.isSelected ? "#1e40af" : "#1f2937",
      cursor: "pointer",
      padding: "10px 12px",
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "#dbeafe",
      borderRadius: "6px",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "#1e40af",
      padding: "3px 6px",
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "#1e40af",
      ":hover": { backgroundColor: "#bfdbfe", color: "#1e3a8a" },
    }),
  };
  const fieldStyle = {
    marginBottom: "16px",
  };

  const labelStyle = {
    display: "block",
    fontSize: "13px",
    fontWeight: "600",
    color: "#374151",
    marginBottom: "6px",
  };

  const inputStyle = {
    width: "100%",
    padding: "11px 14px",
    fontSize: "14px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    outline: "none",
    transition: "all 0.2s",
    boxSizing: "border-box",
  };

  const inputFocusStyle = {
    borderColor: "#3b82f6",
    boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
  };

  return (
    <div
      style={{
        borderRadius: "16px",
        backgroundColor: "white",
        padding: "24px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        marginTop: "24px",
      }}
    >
      <h2
        style={{
          fontSize: "28px",
          fontWeight: "700",
          marginBottom: "8px",
          color: "#111827",
        }}
      >
        User Management
      </h2>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          marginBottom: "24px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "16px",
          }}
        >
          <input
            type="text"
            placeholder="Search by name..."
            value={search}
            onChange={handleSearch}
            style={{
              ...inputStyle,
              width: "300px",
              minWidth: "250px",
            }}
            onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
            onBlur={(e) => {
              e.target.style.borderColor = "#d1d5db";
              e.target.style.boxShadow = "none";
            }}
          />
          {UserMgmt?.canAdd && (
            <button
              onClick={() => {
                setShowAdd(true);
                setEditUserId(null);
                setAddForm(getResetForm());
                setSelectedOrganizations([]);
                setSelectedObjects([]);
              }}
              style={{
                backgroundColor: "#1e40af",
                color: "white",
                padding: "10px 20px",
                borderRadius: "8px",
                border: "none",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "background-color 0.2s",
              }}
              {...createHoverHandlers("#1e3a8a", "#1e40af")}
            >
              <span style={{ fontSize: "20px", fontWeight: "700" }}>+</span> Add
              User
            </button>
          )}
        </div>
      </div>

      {/* Add/Edit User Modal */}
      <UserModal
        show={showAdd}
        title={editUserId ? "Edit User" : "Add User"}
        onClose={() => setShowAdd(false)}
      >
        <div>
          {/* Email */}
          <div style={fieldStyle}>
            <label htmlFor="user-email" style={labelStyle}>
              Email <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              id="user-email"
              type="email"
              placeholder="Enter email address"
              value={addForm.email}
              onChange={(e) =>
                setAddForm((f) => ({ ...f, email: e.target.value }))
              }
              disabled={editUserId !== null}
              style={{
                ...inputStyle,
                backgroundColor: editUserId === null ? "white" : "#f9fafb",
                cursor: editUserId === null ? "text" : "not-allowed",
              }}
              onFocus={(e) =>
                editUserId === null && Object.assign(e.target.style, inputFocusStyle)
              }
              onBlur={(e) => {
                e.target.style.borderColor = "#d1d5db";
                e.target.style.boxShadow = "none";
              }}
            />
          </div>

          {/* First Name */}
          <div style={fieldStyle}>
            <label htmlFor="user-first-name" style={labelStyle}>
              First Name <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              id="user-first-name"
              type="text"
              placeholder="Enter first name"
              value={addForm.firstName}
              onChange={(e) =>
                setAddForm((f) => ({ ...f, firstName: e.target.value }))
              }
              style={inputStyle}
              onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
              onBlur={(e) => {
                e.target.style.borderColor = "#d1d5db";
                e.target.style.boxShadow = "none";
              }}
            />
          </div>

          {/* Last Name */}
          <div style={fieldStyle}>
            <label htmlFor="user-last-name" style={labelStyle}>
              Last Name <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              id="user-last-name"
              type="text"
              placeholder="Enter last name"
              value={addForm.lastName}
              onChange={(e) =>
                setAddForm((f) => ({ ...f, lastName: e.target.value }))
              }
              style={inputStyle}
              onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
              onBlur={(e) => {
                e.target.style.borderColor = "#d1d5db";
                e.target.style.boxShadow = "none";
              }}
            />
          </div>

          {/* Role */}
          <div style={fieldStyle}>
            <label htmlFor="user-role" style={labelStyle}>
              Role <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <select
              id="user-role"
              value={addForm.role}
              onChange={(e) =>
                setAddForm((f) => ({ ...f, role: e.target.value }))
              }
              style={{
                ...inputStyle,
                cursor: "pointer",
              }}
              onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
              onBlur={(e) => {
                e.target.style.borderColor = "#d1d5db";
                e.target.style.boxShadow = "none";
              }}
            >
              <option value="" disabled>
                Select Role
              </option>
              {roles?.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>

          {/* Organisation */}
          <div style={fieldStyle}>
            <label htmlFor="user-organisation" style={labelStyle}>
              Organisation <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <div id="user-organisation">
              <Select
                options={allOrganizations}
                isMulti
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                components={{ Option }}
                onChange={(selected) => setSelectedOrganizations(selected)}
                value={selectedOrganizations}
                placeholder="Select organisations"
                styles={selectStyles}
                menuIsOpen={undefined}
                openMenuOnClick={true}
                isClearable={false}
              />
            </div>
          </div>

          {/* Objects - Temporarily commented out */}
          {/* <div style={fieldStyle}>
            <label htmlFor="user-objects" style={labelStyle}>
              Objects <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <div id="user-objects">
              <Select
                options={allObjects}
                isMulti
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                components={{ Option,MenuList,ValueContainer }}
                onChange={(selected) => setSelectedObjects(selected)}
                value={selectedObjects}
                placeholder={
                  objectsLoading ? "Loading objects..." : "Select objects"
                }
                isDisabled={objectsLoading || allObjects.length === 0}
                styles={selectStyles}
                menuIsOpen={undefined}
                openMenuOnClick={true}
                isClearable={false} 
              />
            </div>
            {selectedOrganizations.length > 0 ? null : (
              <small
                style={{
                  display: "block",
                  marginTop: "6px",
                  color: "#6b7280",
                  fontSize: "13px",
                }}
              >
                Please select at least one organisation first
              </small>
            )}
          </div> */}

          {/* Status */}
          <div style={fieldStyle}>
            <label htmlFor="user-status" style={labelStyle}>
              Status <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <select
              id="user-status"
              value={addForm.isActive}
              onChange={(e) =>
                setAddForm((f) => ({ ...f, isActive: e.target.value }))
              }
              style={{
                ...inputStyle,
                cursor: "pointer",
              }}
              onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
              onBlur={(e) => {
                e.target.style.borderColor = "#d1d5db";
                e.target.style.boxShadow = "none";
              }}
            >
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>

          {/* Action Buttons */}
          <div
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "flex-end",
              marginTop: "32px",
            }}
          >
            <button
              onClick={() => setShowAdd(false)}
              style={{
                padding: "10px 24px",
                borderRadius: "8px",
                border: "1px solid #d1d5db",
                backgroundColor: "white",
                color: "#374151",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              {...createHoverHandlers("#f9fafb", "white")}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={addLoading || !isFormValid()}
              style={{
                padding: "10px 24px",
                borderRadius: "8px",
                border: "none",
                backgroundColor: (addLoading || !isFormValid()) ? "#9ca3af" : "#f97316",
                color: "white",
                fontSize: "14px",
                fontWeight: "600",
                cursor: (addLoading || !isFormValid()) ? "not-allowed" : "pointer",
                transition: "background-color 0.2s",
              }}
              {...(!addLoading && isFormValid() && createHoverHandlers("#ea580c", "#f97316"))}
            >
              {addLoading ? "Saving..." : "Save User"}
            </button>
          </div>
        </div>
      </UserModal>

      {/* Delete Confirmation Modal */}
      <UserModal
        show={showDeleteModal}
        title="Delete User"
        onClose={() => setShowDeleteModal(false)}
      >
        <div>
          <p
            style={{ fontSize: "16px", color: "#6b7280", marginBottom: "24px" }}
          >
            Are you sure you want to delete this user?
          </p>
          <div
            style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}
          >
            <button
              onClick={() => setShowDeleteModal(false)}
              style={{
                padding: "10px 24px",
                borderRadius: "8px",
                border: "1px solid #d1d5db",
                backgroundColor: "white",
                color: "#374151",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "background-color 0.2s",
              }}
              {...createHoverHandlers("#f9fafb", "white")}
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteUser}
              style={{
                padding: "10px 24px",
                borderRadius: "8px",
                border: "none",
                backgroundColor: "#ef4444",
                color: "white",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "background-color 0.2s",
              }}
              {...createHoverHandlers("#dc2626", "#ef4444")}
            >
              Delete
            </button>
          </div>
        </div>
      </UserModal>

      {/* Users Table */}
      {(() => {
        let usersTableContent;
        if (loading) {
          usersTableContent = (
            <div style={{ textAlign: "center", padding: "48px", color: "#6b7280" }}>
              Loading users...
            </div>
          );
        } else if (error) {
          usersTableContent = (
            <div style={{ textAlign: "center", padding: "48px", color: "#ef4444" }}>
              {error}
            </div>
          );
        } else {
          usersTableContent = (
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  backgroundColor: "white",
                  borderRadius: "12px",
                  border: "1px solid #e5e7eb",
                  borderCollapse: "separate",
                  borderSpacing: 0,
                }}
              >
                <thead>
                  <tr
                    style={{
                      background: "linear-gradient(to right, #eff6ff, #dbeafe)",
                    }}
                  >
                    <th
                      onClick={() => setSortAsc(!sortAsc)}
                      style={{
                        padding: "16px 20px",
                        textAlign: "left",
                        fontWeight: "700",
                        color: "#374151",
                        cursor: "pointer",
                        borderBottom: "2px solid #bfdbfe",
                      }}
                    >
                      Name{" "}
                      <span style={{ marginLeft: "8px", fontSize: "12px" }}>
                        {sortAsc ? "▲" : "▼"}
                      </span>
                    </th>
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "left",
                        fontWeight: "700",
                        color: "#374151",
                        borderBottom: "2px solid #bfdbfe",
                      }}
                    >
                      Email
                    </th>
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "left",
                        fontWeight: "700",
                        color: "#374151",
                        borderBottom: "2px solid #bfdbfe",
                      }}
                    >
                      Role
                    </th>
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "left",
                        fontWeight: "700",
                        color: "#374151",
                        borderBottom: "2px solid #bfdbfe",
                      }}
                    >
                      Status
                    </th>
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "left",
                        fontWeight: "700",
                        color: "#374151",
                        borderBottom: "2px solid #bfdbfe",
                      }}
                    >
                      Created Date
                    </th>
                    {(UserMgmt?.canDelete || UserMgmt?.canUpdate) && (
                      <th
                        style={{
                          padding: "16px 20px",
                          textAlign: "left",
                          fontWeight: "700",
                          color: "#374151",
                          borderBottom: "2px solid #bfdbfe",
                        }}
                      >
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {renderUsersTableContent()}
                </tbody>
                <tfoot>
                  <tr>
                    <td
                      colSpan={getTableColSpan()}
                      style={{
                        padding: "16px 20px",
                        textAlign: "right",
                        fontSize: "12px",
                        color: "#6b7280",
                        backgroundColor: "#f9fafb",
                        borderTop: "1px solid #e5e7eb",
                      }}
                    >
                      Showing {pagedUsers.length} of {sortedUsers.length} user{sortedUsers.length === 1 ? "" : "s"}
                    </td>
                  </tr>
                  {totalPages > 1 && (
                    <tr>
                      <td
                        colSpan={getTableColSpan()}
                        style={{
                          padding: "16px 20px",
                          backgroundColor: "white",
                          textAlign: "center",
                        }}
                      >
                        <div
                          style={{
                            display: "inline-flex",
                            gap: "8px",
                            alignItems: "center",
                          }}
                        >
                          <button
                            onClick={() => setPage(page - 1)}
                            disabled={page === 1}
                            style={{
                              padding: "8px 16px",
                              borderRadius: "6px",
                              border: "1px solid #d1d5db",
                              backgroundColor: page === 1 ? "#f3f4f6" : "white",
                              color: page === 1 ? "#9ca3af" : "#374151",
                              cursor: page === 1 ? "not-allowed" : "pointer",
                              fontSize: "14px",
                              fontWeight: "600",
                              transition: "all 0.2s",
                            }}
                            {...(page !== 1 && createHoverHandlers("#eff6ff", "white"))}
                            onFocus={(e) => {
                              if (page !== 1) {
                                e.currentTarget.style.borderColor = "#3b82f6";
                              }
                            }}
                            onBlur={(e) => {
                              if (page !== 1) {
                                e.currentTarget.style.borderColor = "#d1d5db";
                              }
                            }}
                          >
                            Prev
                          </button>

                          {Array.from({ length: totalPages }).map((_, i) => (
                            <button
                              key={`page-${i + 1}`}
                              onClick={() => setPage(i + 1)}
                              style={{
                                padding: "8px 16px",
                                borderRadius: "6px",
                                border: `1px solid ${page === i + 1 ? "#1e40af" : "#d1d5db"
                                  }`,
                                backgroundColor:
                                  page === i + 1 ? "#1e40af" : "white",
                                color: page === i + 1 ? "white" : "#374151",
                                cursor: "pointer",
                                fontSize: "14px",
                                fontWeight: "600",
                                minWidth: "40px",
                                transition: "all 0.2s",
                              }}
                              {...(page !== i + 1 && createHoverHandlers("#eff6ff", "white"))}
                              onFocus={(e) => {
                                if (page !== i + 1) {
                                  e.currentTarget.style.borderColor = "#3b82f6";
                                }
                              }}
                              onBlur={(e) => {
                                if (page !== i + 1) {
                                  e.currentTarget.style.borderColor = "#d1d5db";
                                }
                              }}
                            >
                              {i + 1}
                            </button>
                          ))}

                          <button
                            onClick={() => setPage(page + 1)}
                            disabled={page === totalPages}
                            style={{
                              padding: "8px 16px",
                              borderRadius: "6px",
                              border: "1px solid #d1d5db",
                              backgroundColor:
                                page === totalPages ? "#f3f4f6" : "white",
                              color: page === totalPages ? "#9ca3af" : "#374151",
                              cursor:
                                page === totalPages ? "not-allowed" : "pointer",
                              fontSize: "14px",
                              fontWeight: "600",
                              transition: "all 0.2s",
                            }}
                            {...(page !== totalPages && createHoverHandlers("#eff6ff", "white"))}
                            onFocus={(e) => {
                              if (page !== totalPages) {
                                e.currentTarget.style.borderColor = "#3b82f6";
                              }
                            }}
                            onBlur={(e) => {
                              if (page !== totalPages) {
                                e.currentTarget.style.borderColor = "#d1d5db";
                              }
                            }}
                          >
                            Next
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </tfoot>
              </table>
            </div>
          );
        }
        return usersTableContent;
      })()}
    </div>
  );
}
